import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {DataSet, Network} from 'vis-network/standalone';
import {FiGitBranch, FiMaximize2, FiMove, FiRefreshCw, FiSearch, FiZoomIn, FiZoomOut} from 'react-icons/fi';
import {buildGraphData, expandVisibleIds, getNodeLabel, graphNeighbors, initialVisibleIds, visibleGraph} from './graphLayout';
import './GraphCanvas.css';

const types = {resource: 'منبع', chunk: 'بخش متن', concept: 'مفهوم', assertion: 'رابطه'};
const colors = {resource: '#3b82f6', chunk: '#94a3b8', concept: '#10b981', assertion: '#f97316'};
const relationKey = (edge) => String(edge.record.type || edge.record.predicate || edge.record.relation || '');
const toNetworkNode = ({record, ...node}) => node;
const toNetworkEdge = ({record, ...edge}) => ({...edge, label: ''});

export default function GraphCanvas({graph, selectedId, selectedEdgeId, onNodeSelect, onEdgeSelect}) {
    const graphData = useMemo(() => buildGraphData(graph), [graph]);
    const initialIds = useMemo(() => initialVisibleIds(graphData), [graphData]);
    const [visibleIds, setVisibleIds] = useState(initialIds);
    const [showAll, setShowAll] = useState(false);
    const [nodeType, setNodeType] = useState('');
    const [edgeType, setEdgeType] = useState('');
    const [query, setQuery] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [showLabels, setShowLabels] = useState(true);
    const [labelsAtZoom, setLabelsAtZoom] = useState(false);
    const [hoveredEdgeId, setHoveredEdgeId] = useState(null);
    const [dragMode, setDragMode] = useState('pan');
    const canvasRef = useRef(null);
    const networkRef = useRef(null);
    const nodesRef = useRef(null);
    const edgesRef = useRef(null);
    const pendingFocusRef = useRef(null);
    const fitAfterLayoutRef = useRef(true);
    const renderedLabelsRef = useRef(new Map());
    const callbacksRef = useRef({onNodeSelect, onEdgeSelect});
    callbacksRef.current = {onNodeSelect, onEdgeSelect};

    useEffect(() => {
        setVisibleIds(initialIds);
        setShowAll(false);
        setQuery('');
        setNodeType('');
        setEdgeType('');
    }, [initialIds]);

    const ids = useMemo(() => showAll ? new Set(graphData.nodes.map((node) => node.id)) : visibleIds,
        [graphData, showAll, visibleIds]);
    const display = useMemo(() => visibleGraph(graphData, ids, nodeType, edgeType),
        [graphData, ids, nodeType, edgeType]);
    const relationOptions = useMemo(() => [...new Map(graphData.edges.map((edge) => [relationKey(edge), edge.label])).entries()]
        .filter(([key]) => key).sort((a, b) => a[1].localeCompare(b[1], 'fa')), [graphData]);
    const suggestions = useMemo(() => {
        const term = query.trim().toLocaleLowerCase();
        if (!term) return [];
        return graphData.nodes.filter((node) => `${getNodeLabel(node.record)} ${node.id}`.toLocaleLowerCase().includes(term)).slice(0, 8);
    }, [graphData, query]);
    const hiddenNeighbors = selectedId == null ? 0 : graphNeighbors(graphData, selectedId)
        .filter((id) => !ids.has(id)).length;

    const revealNode = useCallback((node) => {
        setNodeType('');
        setEdgeType('');
        setShowAll(false);
        setVisibleIds((previous) => expandVisibleIds(graphData, previous, node.id, 12));
        pendingFocusRef.current = String(node.id);
        setQuery('');
        setShowSuggestions(false);
        callbacksRef.current.onNodeSelect(node.record);
    }, [graphData]);

    const expandSelected = useCallback(() => {
        if (selectedId == null) return;
        pendingFocusRef.current = String(selectedId);
        setVisibleIds((previous) => expandVisibleIds(graphData, previous, selectedId));
    }, [graphData, selectedId]);

    // Create one network per API response. Subsequent exploration updates its DataSets,
    // preserving positions already adjusted by the user.
    useEffect(() => {
        if (!canvasRef.current || !graphData.nodes.length) return undefined;
        const first = visibleGraph(graphData, initialIds);
        const nodes = new DataSet(first.nodes.map(toNetworkNode));
        const edges = new DataSet(first.edges.map(toNetworkEdge));
        nodesRef.current = nodes;
        edgesRef.current = edges;
        fitAfterLayoutRef.current = true;
        renderedLabelsRef.current = new Map();
        const network = new Network(canvasRef.current, {nodes, edges}, {
            autoResize: true,
            layout: {improvedLayout: false, randomSeed: 11},
            nodes: {borderWidth: 2, borderWidthSelected: 4, font: {face: 'YekanBakh, sans-serif', size: 12, color: '#1e293b'},
                shadow: {enabled: true, color: '#0f172a18', size: 8, x: 0, y: 3}, margin: 8},
            edges: {arrows: {to: {enabled: true, scaleFactor: 0.55}}, width: 1.5,
                color: {color: '#94a3b8', highlight: '#7c3aed', hover: '#7c3aed'},
                font: {face: 'YekanBakh, sans-serif', size: 11, color: '#475569', background: '#ffffffdd', strokeWidth: 0},
                smooth: {enabled: true, type: 'dynamic', roundness: 0.25}, selectionWidth: 2},
            interaction: {hover: true, multiselect: false, dragView: true, dragNodes: false,
                zoomView: false, hideEdgesOnDrag: false, navigationButtons: false,
                keyboard: {enabled: true, bindToWindow: false}},
            physics: {enabled: true, solver: 'barnesHut', barnesHut: {
                gravitationalConstant: -7000, centralGravity: 0.12, springLength: 185,
                springConstant: 0.025, damping: 0.35, avoidOverlap: 0.5,
            }, stabilization: {enabled: true, iterations: 180, updateInterval: 50, fit: false}},
        });
        networkRef.current = network;
        const syncBackground = () => {
            if (!canvasRef.current) return;
            const origin = network.canvasToDOM({x: 0, y: 0});
            const spacing = Math.max(12, Math.min(44, 22 * network.getScale()));
            canvasRef.current.style.backgroundSize = `${spacing}px ${spacing}px`;
            canvasRef.current.style.backgroundPosition = `${origin.x}px ${origin.y}px`;
        };
        syncBackground();
        network.on('dragStart', () => canvasRef.current?.classList.add('is-panning'));
        network.on('dragging', syncBackground);
        network.on('dragEnd', () => { canvasRef.current?.classList.remove('is-panning'); syncBackground(); });
        network.on('animationFinished', syncBackground);
        network.on('resize', syncBackground);
        network.on('selectNode', ({nodes: selected}) => {
            const node = graphData.nodes.find((item) => item.id === selected[0]);
            if (node) callbacksRef.current.onNodeSelect(node.record);
        });
        network.on('selectEdge', ({edges: selected, nodes: selectedNodes}) => {
            if (selectedNodes.length) return;
            const edge = graphData.edges.find((item) => item.id === selected[0]);
            if (edge) callbacksRef.current.onEdgeSelect(edge);
        });
        network.on('doubleClick', ({nodes: selected}) => {
            if (selected[0]) {
                pendingFocusRef.current = selected[0];
                setVisibleIds((previous) => expandVisibleIds(graphData, previous, selected[0]));
            }
        });
        network.on('hoverEdge', ({edge}) => setHoveredEdgeId(edge));
        network.on('blurEdge', () => setHoveredEdgeId(null));
        network.on('zoom', ({scale}) => { setLabelsAtZoom(scale >= 0.65); syncBackground(); });
        network.on('stabilizationIterationsDone', () => {
            network.setOptions({physics: {enabled: false}});
            const focus = pendingFocusRef.current;
            pendingFocusRef.current = null;
            const shouldFit = fitAfterLayoutRef.current;
            fitAfterLayoutRef.current = false;
            if (focus && nodes.get(focus)) network.focus(focus, {scale: Math.max(network.getScale(), 0.8), locked: false, animation: true});
            else if (shouldFit) network.fit({animation: {duration: 250, easingFunction: 'easeInOutQuad'}});
            setLabelsAtZoom(network.getScale() >= 0.65);
            syncBackground();
        });
        return () => { network.destroy(); networkRef.current = null; nodesRef.current = null; edgesRef.current = null; };
    }, [graphData, initialIds]);

    useEffect(() => {
        networkRef.current?.setOptions({interaction: {dragView: true, dragNodes: dragMode === 'nodes', zoomView: selectedId != null}});
    }, [dragMode, selectedId]);

    useEffect(() => {
        const nodes = nodesRef.current;
        const edges = edgesRef.current;
        const network = networkRef.current;
        if (!nodes || !edges || !network) return;
        const newNodeIds = new Set(display.nodes.map((node) => node.id));
        const newEdgeIds = new Set(display.edges.map((edge) => edge.id));
        const removedEdges = edges.getIds().filter((id) => !newEdgeIds.has(id));
        const removedNodes = nodes.getIds().filter((id) => !newNodeIds.has(id));
        const addedNodes = display.nodes.filter((node) => !nodes.get(node.id)).map(toNetworkNode);
        const addedEdges = display.edges.filter((edge) => !edges.get(edge.id)).map(toNetworkEdge);
        if (!removedEdges.length && !removedNodes.length && !addedNodes.length && !addedEdges.length) return;
        fitAfterLayoutRef.current = true;
        edges.remove(removedEdges);
        nodes.remove(removedNodes);
        nodes.add(addedNodes);
        edges.add(addedEdges);
        network.setOptions({physics: {enabled: true}});
        network.stabilize(120);
    }, [display]);

    useEffect(() => {
        if (!edgesRef.current) return;
        const labelsVisible = showLabels && labelsAtZoom;
        const rendered = renderedLabelsRef.current;
        const currentIds = new Set(display.edges.map((edge) => edge.id));
        for (const id of rendered.keys()) if (!currentIds.has(id)) rendered.delete(id);
        const changes = display.edges.flatMap((edge) => {
            const label = labelsVisible || edge.id === hoveredEdgeId || edge.id === selectedEdgeId ? edge.label : '';
            if (rendered.get(edge.id) === label) return [];
            rendered.set(edge.id, label);
            return [{id: edge.id, label}];
        });
        if (changes.length) edgesRef.current.update(changes);
    }, [display, showLabels, labelsAtZoom, hoveredEdgeId, selectedEdgeId]);

    useEffect(() => {
        const network = networkRef.current;
        const nodes = nodesRef.current;
        const edges = edgesRef.current;
        if (!network) return;
        if (selectedId != null && nodes?.get(String(selectedId))) network.selectNodes([String(selectedId)], false);
        else if (selectedEdgeId != null && edges?.get(selectedEdgeId)) network.selectEdges([selectedEdgeId]);
        else network.unselectAll();
    }, [selectedId, selectedEdgeId, display]);

    if (!graphData.nodes.length) return <div className="kg-empty-graph"><FiGitBranch/><h3>گره‌ای برای نمایش وجود ندارد</h3></div>;
    return <div className="kg-flow-shell" dir="rtl">
        <div className="kg-flow-toolbar">
            <div className="kg-flow-legend">{Object.entries(types).map(([type, label]) => <span key={type}><i style={{background: colors[type]}}/>{label}</span>)}</div>
            <span className="kg-flow-count">{display.nodes.length.toLocaleString('fa-IR')} از {graphData.nodes.length.toLocaleString('fa-IR')} گره</span>
            <div className="kg-flow-actions">
                <button type="button" className="kg-flow-drag-mode" aria-pressed={dragMode === 'pan'} onClick={() => setDragMode((mode) => mode === 'pan' ? 'nodes' : 'pan')}><FiMove/>{dragMode === 'pan' ? 'حرکت نما' : 'حرکت گره‌ها'}</button>
                <button type="button" aria-label="بزرگ‌نمایی" onClick={() => networkRef.current?.moveTo({scale: Math.min(networkRef.current.getScale() * 1.3, 3), animation: true})}><FiZoomIn/></button>
                <button type="button" aria-label="کوچک‌نمایی" onClick={() => networkRef.current?.moveTo({scale: Math.max(networkRef.current.getScale() / 1.3, 0.1), animation: true})}><FiZoomOut/></button>
                <button type="button" aria-label="نمایش همه گره‌های موجود" onClick={() => networkRef.current?.fit({animation: true})}><FiMaximize2/></button>
                <button type="button" onClick={() => { pendingFocusRef.current = null; fitAfterLayoutRef.current = true; networkRef.current?.setOptions({physics: {enabled: true}}); networkRef.current?.stabilize(180); }}><FiRefreshCw/>بازچینی</button>
            </div>
        </div>
        <div className="kg-flow-filters">
            <div className="kg-flow-search" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setShowSuggestions(false); }}><FiSearch/><input value={query} onChange={(event) => { setQuery(event.target.value); setShowSuggestions(true); }} onFocus={() => setShowSuggestions(true)} onKeyDown={(event) => {
                if (event.key === 'Enter' && suggestions.length) revealNode(suggestions[0]);
                if (event.key === 'Escape') setShowSuggestions(false);
            }} placeholder="جست‌وجوی مفهوم یا شناسه در کل گراف" aria-label="جست‌وجوی گره"/>
                {showSuggestions && query.trim() && <div className="kg-flow-suggestions">{suggestions.length ? suggestions.map((node) => <button key={node.id} type="button" onClick={() => revealNode(node)}><strong>{getNodeLabel(node.record)}</strong><small>{types[node.record.type] || 'گره'}</small></button>) : <p>نتیجه‌ای پیدا نشد.</p>}</div>}
            </div>
            <select value={nodeType} onChange={(event) => setNodeType(event.target.value)} aria-label="فیلتر نوع گره"><option value="">همه گره‌ها</option>{Object.entries(types).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select>
            <select value={edgeType} onChange={(event) => setEdgeType(event.target.value)} aria-label="فیلتر نوع رابطه"><option value="">همه رابطه‌ها</option>{relationOptions.map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select>
            <label className="kg-check kg-flow-label-toggle"><input type="checkbox" checked={showLabels} onChange={(event) => setShowLabels(event.target.checked)}/><span>نام یال‌ها</span></label>
            <button type="button" className="kg-flow-mode" onClick={() => { setShowAll((value) => !value); pendingFocusRef.current = null; }}>{showAll ? 'نمای گزیده' : 'نمایش همه'}</button>
            {selectedId != null && !showAll && <button type="button" className="kg-flow-expand" disabled={!hiddenNeighbors} onClick={expandSelected}>گسترش همسایه‌ها {hiddenNeighbors ? `(${hiddenNeighbors.toLocaleString('fa-IR')} باقی)` : ''}</button>}
        </div>
        <div className="kg-flow-canvas-wrap"><div ref={canvasRef} className="kg-flow-canvas" role="application" tabIndex="0" aria-label="گراف تعاملی مفاهیم و روابط"/>
            {!display.nodes.length && <div className="kg-flow-no-results">با این فیلتر، گره‌ای در نمای فعلی دیده نمی‌شود. فیلتر را تغییر دهید یا «نمایش همه» را بزنید.</div>}</div>
        <p className="kg-flow-hint">با کشیدن موس، نما جابه‌جا می‌شود؛ {selectedId != null ? 'پس از انتخاب گره، با رول موس روی گراف زوم کنید.' : 'برای زوم با رول موس، ابتدا یک گره را انتخاب کنید.'} برای تغییر جای یک گره، حالت «حرکت گره‌ها» را انتخاب کنید.</p>
    </div>;
}
